/** The warroom shell (IA.md §Warroom, DESIGN.md §Layout).
 *
 * left = facility-radar map (the signature, given real estate) + KPI strip
 * center = feed wall
 * right = live alert queue + selected-entity mini-dossier
 *
 * The five panels are owned by sibling lanes. They are mounted by discovery rather than by
 * static import: `import.meta.glob` resolves at build time to whatever actually exists on
 * disk, so a panel that has not landed yet leaves an inert, labelled slot instead of
 * breaking the build for everyone else. Drop `src/panels/RadarMap.tsx` in and it mounts.
 */

import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import type { ComponentType, ReactNode } from 'react'
import { subscribe, useFlags, useSelected, useWorldSnapshot } from './lib/ws.ts'
import { isStatic } from './lib/static.ts'
import { demoInfo, fmtProvenance } from './lib/api.ts'
import type { DemoInfo } from './lib/api.ts'
import type { Flag, Severity, Viewport } from './lib/types.ts'

const PANELS = ['RadarMap', 'FeedWall', 'AlertRail', 'Dossier', 'Triage'] as const
type PanelName = (typeof PANELS)[number]

const MODULES: Record<string, () => Promise<unknown>> = {
  ...import.meta.glob('./panels/*.tsx'),
  ...import.meta.glob('./panels/*/index.tsx'),
}

/** An inert slot. It says what is missing — it never stands in for data. */
function Slot({ name, path, note, error, children }: {
  name: string; path: string; note: string; error?: boolean; children?: ReactNode
}) {
  return (
    <div className={error ? 'slot slot--error' : 'slot'} role={error ? 'alert' : 'status'}>
      <span className="slot__name">{name}</span>
      <span className="slot__path">{path}</span>
      <span className="slot__note">{note}</span>
      {children}
    </div>
  )
}

const Pending = ({ name }: { name: PanelName }) => (
  <Slot
    name={name}
    path={`src/panels/${name}.tsx`}
    note="Panel not mounted yet. Nothing is being shown here — this is an empty slot, not data."
  />
)

/** Chunk in flight — different truth from Pending (which means nothing exists on disk). */
const Loading = ({ name }: { name: PanelName }) => (
  <Slot name={name} path={`src/panels/${name}.tsx`} note={`Loading ${name}…`} />
)

// Panels are sibling-owned; the shell passes their documented props without importing their
// types (loose coupling keeps one lane's type break from stopping the other three).
function mount(name: PanelName): ComponentType<any> {
  const load = MODULES[`./panels/${name}.tsx`] ?? MODULES[`./panels/${name}/index.tsx`]
  if (!load) return () => <Pending name={name} />
  // Accept a default export or a named export matching the panel name, so a sibling lane's
  // export style cannot break integration.
  return lazy(async () => {
    const mod = (await load()) as Record<string, ComponentType<any>>
    return { default: mod.default ?? mod[name] ?? (() => <Pending name={name} />) }
  })
}

const RadarMap = mount('RadarMap')
const FeedWall = mount('FeedWall')
const AlertRail = mount('AlertRail')
const Dossier = mount('Dossier')
const Triage = mount('Triage')

/** One panel failing must never take the warroom down with it. */
class Guard extends Component<{ name: PanelName; children: ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null }
  static getDerivedStateFromError(err: Error) { return { err } }
  render() {
    const { err } = this.state
    if (!err) return this.props.children
    return (
      <Slot
        error
        name={`${this.props.name} stopped`}
        path={err.message}
        note="This panel crashed. The rest of the warroom is still live."
      >
        <button className="slot__retry" onClick={() => this.setState({ err: null })}>
          Retry {this.props.name}
        </button>
      </Slot>
    )
  }
}

const Region = ({ name, split, children }: {
  name: PanelName; split?: boolean; children: ReactNode
}) => (
  <div className={split ? 'col__region col__region--split' : 'col__region'}>
    <Guard name={name}>
      <Suspense fallback={<Loading name={name} />}>{children}</Suspense>
    </Guard>
  </div>
)

// ---------------------------------------------------------------- KPI strip

const SEVERITIES: Severity[] = ['note', 'flag', 'red-flag']

function tally(flags: Flag[]): Record<Severity, number> {
  const out: Record<Severity, number> = { note: 0, flag: 0, 'red-flag': 0 }
  for (const f of flags) if (f.severity in out) out[f.severity] += 1
  return out
}

const Kpi = ({ label, value, tone }: { label: string; value: number; tone?: string }) => (
  <div className="kpi">
    <span className="kpi__label">{label}</span>
    <span className="kpi__value" data-tone={value === 0 ? 'zero' : tone}>{value}</span>
  </div>
)

/** Collapsed rail: one fat expand target carrying the severity counts — an operator who
 *  hides the sidebar must never hide the alarm. `key` remounts the red badge on a new
 *  red-flag so the arrival pulses once (reduced-motion collapses it to instant state). */
function RailStrip({ onExpand }: { onExpand: () => void }) {
  const sev = tally(useFlags())
  const red = sev['red-flag']
  return (
    <button
      className="rail-strip"
      onClick={onExpand}
      aria-expanded="false"
      aria-controls="rail"
      aria-label={`Expand alerts rail — ${red} red flags, ${sev.flag} flags, ${sev.note} notes`}
      title="Expand alerts rail"
    >
      <span className="rail-strip__chev" aria-hidden="true">&#x2039;</span>
      {red > 0 && <span key={red} className="rail-badge rail-badge--red">{red} red</span>}
      {sev.flag > 0 && <span className="rail-badge">{sev.flag} flag</span>}
    </button>
  )
}

/** `counts.*` are backend measurements. The three severity figures are NOT: they tally the
 *  live rail — the newest <=500 findings (ws.ts MAX_FLAGS) received since this tab connected
 *  (the server never replays findings fired before connect). Recent activity, not facility
 *  totals; the figures can fall as old findings age out. */
function Kpis() {
  const { counts } = useWorldSnapshot()
  const sev = tally(useFlags())
  return (
    <div className="kpis" aria-label="Facility key figures">
      <Kpi label="people" value={counts.persons} tone="calm" />
      <Kpi label="items" value={counts.items} tone="calm" />
      <Kpi label="unattended" value={counts.unattended_items} tone="warn" />
      <Kpi label="groups" value={counts.groups} />
      <Kpi label="recent notes" value={sev.note} tone="calm" />
      <Kpi label="recent flags" value={sev.flag} tone="warn" />
      <Kpi label="recent red flags" value={sev['red-flag']} tone="alarm" />
    </div>
  )
}

// ---------------------------------------------------------------- shell

export default function App() {
  const world = useWorldSnapshot()
  const { counts, connected, stale, truncated, t, altitude } = world

  // The signature loop (DESIGN.md): cameras covering the selected target, computed by the
  // map's geometry, reframe their wall tiles amber — the map and the wall agree in one
  // glance. RadarMap fires only when the SET changes, so this state updates at selection/
  // movement cadence, never per animation frame.
  const [coveringCams, setCoveringCams] = useState<string[]>([])

  // The anchor of the selection — the one target the dossier is about. The shell reads it
  // only to tell the dossier whether ITS route is the one currently on the map.
  const dossierGid = useSelected()

  // Backend decimation follows the map's viewport (the server must know what the operator
  // is looking at to keep the firehose capped without cutting what is on screen).
  const onViewport = useCallback((vp: Viewport | null) => subscribe({ viewport: vp }), [])

  // Collapsed rail = UI preference (localStorage; not operator evidence, the triage-ledger
  // rule does not apply). The wall reflows by grid-column change only — the center column's
  // React position is untouched, so <video> elements never remount, and the width change
  // fires warroom-fix's ResizeObserver like any real resize.
  const [railCollapsed, setRailCollapsed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('airsec.railCollapsed') === '1',
  )
  const toggleRail = useCallback(() => {
    setRailCollapsed((c) => {
      const next = !c
      try { localStorage.setItem('airsec.railCollapsed', next ? '1' : '0') } catch { /* private mode */ }
      return next
    })
  }, [])

  // ── the scrub wire (warroom-17) ────────────────────────────────────────────────
  // Timeline and Dossier both forwarded `onSeek` and the shell mounted `<Dossier />`
  // BARE, so every scrub was a no-op: the operator dragged the timeline and the videos
  // free-ran (verified: 68.87 → 69.57 over a 700 ms wait). One prop, and then the wall
  // has to be allowed to say NO — see `seekResult`.
  const [seek, setSeek] = useState<{ t: number; nonce: number } | null>(null)
  // Map camera-click -> the wall expands that tile (fire17 2026-08-18). Nonce so the same
  // camera clicked twice re-expands after the operator collapsed it by hand.
  const [focusCam, setFocusCam] = useState<{ id: string; nonce: number } | null>(null)
  // RESTART BACKEND (fire17 2026-08-18): POST /ops/restart kills the server; run_stable.sh
  // relaunches it; we poll /demo/info until it answers, then hard-reload the page so every
  // tile re-attaches. 'dead' after ~60s of silence = the wrapper loop is not running.
  const [restarting, setRestarting] = useState<null | 'waiting' | 'dead'>(null)
  const restartBackend = async () => {
    if (restarting === 'waiting') return
    setRestarting('waiting')
    try { await fetch('/ops/restart', { method: 'POST' }) } catch { /* it died mid-reply — fine */ }
    const t0 = Date.now()
    const poll = () => {
      fetch('/demo/info', { cache: 'no-store' })
        .then((r) => { if (r.ok) location.reload(); else throw new Error() })
        .catch(() => {
          if (Date.now() - t0 > 60_000) setRestarting('dead')
          else setTimeout(poll, 2000)
        })
    }
    setTimeout(poll, 3000)
  }
  const [seekResult, setSeekResult] = useState<{ mode: 'scrubbed' | 'live' | 'refused'; t?: number; reason?: string } | null>(null)
  const onSeek = useCallback((t: number) => {
    // Nonce, not the bare time: scrubbing back to the same instant is a real request.
    setSeek((s) => ({ t, nonce: (s?.nonce ?? 0) + 1 }))
  }, [])
  const resumeLive = useCallback(() => { setSeek(null); setSeekResult(null) }, [])

  // ── promoted historical routes (V7) ────────────────────────────────────────────
  // The dossier's path trace, drawn on the FACILITY MAP instead of a 34 mm thumbnail.
  // The shell owns the list for the same reason it owns `coveringCams`: two panels in
  // two columns agreeing about one fact, and neither importing the other.
  const [routes, setRoutes] = useState<Array<{ gid: string; points: { t: number; x: number; y: number }[] }>>([])
  const showRouteOnMap = useCallback((gid: string, route: { t: number; x: number; y: number }[] | null) => {
    setRoutes((rs) => (route === null ? rs.filter((r) => r.gid !== gid)
      : [...rs.filter((r) => r.gid !== gid), { gid, points: route }]))
  }, [])
  const clearRoutes = useCallback(() => setRoutes([]), [])
  // AUTO-TRAIL (fire17 2026-08-18): selecting a person — on the map or on a feed — shows
  // their WHOLE recorded path, entry to now, not just the live 24s ribbon. Same promoted-
  // route lane the dossier button uses, driven by selection: one fetch of the history
  // store per selection change, previous auto-trail replaced, cleared on deselect.
  const autoTrailGid = useRef<string | null>(null)
  useEffect(() => {
    const prev = autoTrailGid.current
    if (prev && prev !== dossierGid) showRouteOnMap(prev, null)
    autoTrailGid.current = dossierGid ?? null
    if (!dossierGid) return
    let alive = true
    fetch(`/history/person/${encodeURIComponent(dossierGid)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((tl: { tracks?: { t: number; x: number; y: number }[] }) => {
        if (!alive || autoTrailGid.current !== dossierGid) return
        const pts = (tl.tracks ?? []).map((s) => ({ t: s.t, x: s.x, y: s.y }))
        if (pts.length >= 2) showRouteOnMap(dossierGid, pts)
      })
      .catch(() => { /* no history yet — the live ribbon still shows */ })
    return () => { alive = false }
  }, [dossierGid, showRouteOnMap])

  // Provenance of the serving backend — measured, never asserted. Re-fetched on every
  // connection flip so a restarted server (possibly a DIFFERENT rung on the same port)
  // re-states what it is; while unreachable it renders "unknown", never a stale default.
  const [info, setInfo] = useState<DemoInfo | null>(null)
  useEffect(() => {
    let on = true
    demoInfo().then((i) => { if (on) setInfo(i) })
    return () => { on = false }
  }, [connected])

  return (
    <div className="warroom">
      <header className="bar">
        <h1 className="wordmark" aria-label="AirSec Warroom — live facility ground control">
          <b>AirSec</b> Warroom<span>live facility ground control</span>
        </h1>
        <span className="bar__spacer" />
        <a
          href={isStatic() ? '../' : 'http://127.0.0.1:8151/preview/'}
          title={isStatic()
            ? 'The standalone preview — the same scenario as a self-contained page'
            : 'The airsec.akeyo.io preview demo, latest copy served locally — it links back here'}
          style={{
            color: '#7fd4ff', border: '1px solid #2a5a7a', borderRadius: 6,
            padding: '9px 16px', font: '600 13px/1 var(--mono, monospace)',
            letterSpacing: '0.05em', textDecoration: 'none', marginRight: 10,
          }}
        >
          PREVIEW ⟶
        </a>
        <button
          type="button"
          onClick={restartBackend}
          disabled={restarting === 'waiting'}
          title="Kill and relaunch the demo backend (run_stable.sh loop), then reload this page"
          style={{
            background: restarting === 'dead' ? '#7a1f1f' : restarting ? '#5a4a1a' : '#a02020',
            color: '#fff', border: '1px solid #ff6b6b', borderRadius: 6,
            padding: '10px 22px', font: '700 15px/1 var(--mono, monospace)',
            letterSpacing: '0.06em', cursor: restarting === 'waiting' ? 'wait' : 'pointer',
          }}
        >
          {restarting === 'waiting' ? 'RESTARTING… (waits, then reloads)'
            : restarting === 'dead' ? 'BACKEND DID NOT COME BACK — run scripts/run_stable.sh'
            : isStatic() ? 'RELOAD REPLAY'
            : 'RESTART BACKEND'}
        </button>
        {/* Which rung is live, at a glance — fire17 flips between 8137/8138 constantly. */}
        <span className="rungchip" data-known={info?.rung != null}>
          {info?.rung ? `rung ${String(info.rung).toUpperCase()}` : 'rung ?'}
        </span>
        <span className="mono" style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
          t {t.toFixed(1)}s
        </span>
        <span className="link" role="status" data-on={connected && !stale} data-stale={stale}
              title={isStatic()
                ? 'This deployment replays a recorded session — the world is real footage and real tracking, captured, not streaming now.'
                : undefined}>
          {!connected ? (isStatic() ? 'loading replay' : 'reconnecting')
            : stale ? 'no feed'
            : isStatic() ? 'replay' : 'live'}
        </span>
      </header>

      <main className="grid" data-rail={railCollapsed ? 'collapsed' : 'open'}>
        <section className="col col--map" aria-label="Facility map">
          <Region name="RadarMap">
            <RadarMap
              onCameraClick={(id: string) => setFocusCam({ id, nonce: performance.now() })}
              onCoveringCameras={setCoveringCams}
              onViewportChange={onViewport}
              routes={routes}
              onClearRoutes={clearRoutes}
            />
          </Region>
          <Kpis />
        </section>

        <section className="col col--wall" aria-label="Camera feeds">
          <Region name="FeedWall">
            <FeedWall
              focusCamera={focusCam}
              highlightCameras={coveringCams}
              seek={seek}
              onSeekResult={setSeekResult}
            />
          </Region>
          {/* The wall's answer to a scrub, in the operator's line of sight. A REFUSAL is
              the important case and gets the same weight as a success: the alternative is
              a timeline that appears to work while the video shows a different minute. */}
          {seekResult && seekResult.mode !== 'live' && (
            <div className="scrubbar" role="status" data-mode={seekResult.mode}>
              {seekResult.mode === 'scrubbed' ? (
                <>
                  <b>scrubbed</b> to t {seekResult.t?.toFixed(1)}s · live sync paused ·
                  projected boxes hidden (they describe now, not then)
                </>
              ) : (
                <>
                  <b>wall did not seek</b> — {seekResult.reason}
                </>
              )}
              <button className="scrubbar__btn" onClick={resumeLive}>resume live</button>
            </div>
          )}
        </section>

        <aside className="col col--rail" id="rail" aria-label="Alerts, triage and selected entity">
          {railCollapsed ? (
            <RailStrip onExpand={toggleRail} />
          ) : (
            <>
              <button
                className="rail-toggle"
                onClick={toggleRail}
                aria-expanded="true"
                aria-controls="rail"
                title="Collapse alerts rail"
              >
                <span aria-hidden="true">&#x203A;</span>
              </button>
              <Region name="AlertRail"><AlertRail /></Region>
              <Region name="Triage" split><Triage /></Region>
              <Region name="Dossier" split>
                <Dossier
                  onSeek={onSeek}
                  onShowRouteOnMap={showRouteOnMap}
                  routeOnMap={routes.some((r) => r.gid === dossierGid)}
                />
              </Region>
            </>
          )}
        </aside>
      </main>

      <footer className="bar bar--status">
        <span className="mono" title={info?.caveats?.length ? info.caveats.join('\n') : undefined}>
          {fmtProvenance(info)}
          {info?.caveats?.length ? ` · ${info.caveats.length} caveats (hover)` : ''}
        </span>
        <span className="mono">altitude {altitude}</span>
        <span className="mono">
          {truncated
            ? `showing ${counts.returned} of ${counts.total} tracked `
            : `${counts.total} tracked`}
          {/* Inner region is always mounted so AT announces the STATE flip exactly once;
              wrapping the counts would narrate every broadcast. */}
          <span role="status">{truncated ? '(backend decimation on)' : ''}</span>
        </span>
        <span className="bar__spacer" />
        <span className="mono">
          {SEVERITIES.length} severity tiers &middot; colour states meaning, never alone
        </span>
      </footer>
    </div>
  )
}
